import {
    Box,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Stack,
    Typography,
    SxProps,
} from '@mui/material'
import { forwardRef, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    TableVirtuoso,
    TableComponents,
    TableVirtuosoHandle,
} from 'react-virtuoso'

export const OperationsTableCell = styled(TableCell)(({ theme }) => ({
    color: 'text.primary',
    padding: '0',
    paddingTop: '2px',
    '& .MuiInputBase-root': {
        height: '100%',
        backgroundColor: 'transparent',
        borderRadius: 0,
    },
    '& .MuiInputBase-input': {
        padding: '6px 8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
}))

export const OperationsTableHeader = styled(OperationsTableCell)(
    ({ theme }) => ({
        textAlign: 'center',
        fontWeight: 600,
        borderBottom: 'none',
    })
)

const VirtuosoTableComponents: TableComponents = {
    Scroller: TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    /* EmptyPlaceholder: () => {
        return (
            <Stack
                alignItems="center"
                justifyContent="center"
                width={'100%'}
                height={'100%'}
                flex={1}
            >
                <Typography fontSize={'24px'} fontWeight={'500'}>
                    La lista de productos esta vacia
                </Typography>
            </Stack>
        )
    }, */
}

interface Props {
    data: any[]
    renderCell: (index: number, data: any) => JSX.Element
    headers: { name: string; width?: string; hide?: boolean }[]
    sx?: SxProps
    variant?: 'modern' | 'legacy'
    headerColor?: string
    headerTextColor?: string
}

export default function OperationsTable({
    data,
    headers,
    renderCell,
    sx,
    variant = 'legacy',
    headerColor,
    headerTextColor,
}: Props) {
    const { t } = useTranslation()
    const productsTableRef = useRef<TableVirtuosoHandle>(null)
    const isLegacy = variant === 'legacy'

    const resolvedHeaderColor = headerColor ?? (
        isLegacy ? 'rgb(220, 20, 60)' : 'primary.main'
    )
    const resolvedHeaderTextColor = headerTextColor ?? '#FFFFFF'

    useEffect(() => {
        setTimeout(() => {
            if (productsTableRef.current && data.length)
                productsTableRef.current.scrollToIndex(data.length - 1)
        }, 100)
    }, [data.length])

    function fixedHeaderContent() {
        return (
            <TableRow>
                {headers.filter(h => !h.hide).map((header, index) => (
                    <TableCell
                        key={index}
                        component="th"
                        scope="col"
                        sx={{
                            width: header.width,
                            padding: isLegacy ? '5px 0' : '8px 10px',
                            fontSize: isLegacy ? '16px' : '0.875rem',
                            textAlign: 'center',
                            fontWeight: isLegacy ? 700 : 600,
                            whiteSpace: isLegacy ? 'normal' : 'nowrap',
                            backgroundColor: resolvedHeaderColor,
                            color: resolvedHeaderTextColor,
                            borderBottom: 'none',
                            borderRight:
                                index === headers.length - 1
                                    ? 'none'
                                    : isLegacy
                                      ? '1px solid black'
                                      : '1px solid rgba(255, 255, 255, 0.25)',
                        }}
                    >
                        {t(header.name)}
                    </TableCell>
                ))}
            </TableRow>
        )
    }

    return (
        <Box
            sx={{
                height: 400,
                width: '100%',
                my: '15px',
                ...(isLegacy
                    ? {
                          border: '1px solid black',
                      }
                    : {
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow:
                              '0 1px 3px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04)',
                          backgroundColor: 'var(--surface)',
                      }),
                '& .MuiTableContainer-root': {
                    borderRadius: 0,
                },
                ...(isLegacy
                    ? {
                          '& .MuiTableBody-root .MuiTableCell-root': {
                              borderBottom: '1px solid black',
                              borderRight: '1px solid black',
                              padding: '0',
                              paddingTop: '2px',
                          },
                      }
                    : {
                          '& .MuiTableBody-root .MuiTableCell-root': {
                              borderBottom: '1px solid var(--border-soft)',
                              borderRight: 'none',
                              padding: '0',
                          },
                      }),
                ...sx,
            }}
        >
            <TableVirtuoso
                data={data}
                components={VirtuosoTableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={renderCell}
                ref={productsTableRef}
            />
        </Box>
    )
}
